import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import type {
    Beneficiary,
    CreateBeneficiaryPayload,
    UpdateBeneficiaryPayload,
} from '../types/LegalCase';
import { BENEFICIARY_FILTER_DEFS } from '../components/pages/recovery/shared/expenseFilterTypes';
import {
    emptyFilterValues,
    toQueryParams,
    type FilterValues,
} from '../components/pages/recovery/shared/filterTypes';
import type { ErrorField } from '../types/ErrorField';

// Gerencia o registro global de beneficiários: lista (com busca e filtro por tipo), cadastra,
// edita e remove. Usado tanto pela página top-level de Beneficiários quanto pelo select da
// despesa (que precisa listar e, com o "+ Novo", cadastrar sem sair da tela). A remoção pode
// falhar com 409 quando há despesa vinculada — a mensagem do backend é exibida.
export default function useBeneficiaries() {
    const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [search, setSearch] = useState('');
    // Filtros em drawer (só tipo, por ora) no mesmo modelo data-driven da grid de Casos.
    const [filterValues, setFilterValues] = useState<FilterValues>(() =>
        emptyFilterValues(BENEFICIARY_FILTER_DEFS),
    );

    const filterKey = JSON.stringify(toQueryParams(BENEFICIARY_FILTER_DEFS, filterValues));

    const fetchBeneficiaries = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await recoveryApi.getBeneficiaries({
                search: search || undefined,
                ...toQueryParams(BENEFICIARY_FILTER_DEFS, filterValues),
            });
            setBeneficiaries(result.items || []);
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao carregar beneficiários');
        } finally {
            setIsLoading(false);
        }
        // filterValues entra via filterKey (string estável).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterKey]);

    useEffect(() => { fetchBeneficiaries(); }, [fetchBeneficiaries]);

    const setFilterValue = (id: string, value: string | boolean) => {
        setFilterValues((prev) => ({ ...prev, [id]: value }));
    };

    const clearFilters = () => setFilterValues(emptyFilterValues(BENEFICIARY_FILTER_DEFS));

    const clearErrors = () => setErrorFields([]);

    // Cadastra e devolve o beneficiário criado (o select da despesa usa o id para já
    // selecioná-lo). Null em erro.
    const addBeneficiary = async (
        data: CreateBeneficiaryPayload,
    ): Promise<Beneficiary | null> => {
        setIsSaving(true);
        setErrorFields([]);
        try {
            const created = await recoveryApi.createBeneficiary(data);
            await fetchBeneficiaries();
            toast.success('Beneficiário cadastrado');
            return { ...(created as any), id: created.uuid, name: data.name } as Beneficiary;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return null;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao cadastrar beneficiário');
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    const updateBeneficiary = async (
        uuid: string,
        data: UpdateBeneficiaryPayload,
    ): Promise<boolean> => {
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateBeneficiary(uuid, data);
            await fetchBeneficiaries();
            toast.success('Beneficiário atualizado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar beneficiário');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeBeneficiary = async (uuid: string): Promise<boolean> => {
        setIsSaving(true);
        try {
            await recoveryApi.removeBeneficiary(uuid);
            await fetchBeneficiaries();
            toast.success('Beneficiário removido');
            return true;
        } catch (e: any) {
            // 409: beneficiário referenciado por despesa — mostra a mensagem do backend.
            toast.error(e?.data?.message || e?.message || 'Erro ao remover beneficiário');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    return {
        beneficiaries, isLoading, isSaving, errorFields,
        search, setSearch,
        filterValues, setFilterValue, clearFilters,
        addBeneficiary, updateBeneficiary, removeBeneficiary, clearErrors, refresh: fetchBeneficiaries,
    };
}
